import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import { CommsPauseCard } from '@/components/admin/CommsPause';
import { SynergySftpCard } from '@/components/admin/SynergySftpCard';
import { VoiceVerificationCard, useVoiceMode } from '@/components/admin/VoiceVerificationCard';
import { useCommsPause } from '@/hooks/admin/useCommsPause';
import {
  ActionsTd,
  Attention,
  PayTable,
  PayTabs,
  TableState,
  Td,
  Th,
  type PayTab,
} from '@/components/admin/PaymentsLayout';
import { InfoDot } from '@/components/ui/InfoDot';
import {
  useCompanySettings,
  useUpdateCompanySettings,
  type CompanySettingsPatch,
  type ReminderStage,
  type OrderFees,
  type SeaSsbtSetting,
  type CompanySettingsEnv,
} from '@/hooks/admin/useCompanySettings';

type CompanySettingsData = NonNullable<ReturnType<typeof useCompanySettings>['data']>;

const DEFAULT_FEES: OrderFees = {
  fuel: { enabled: false, price: 12.5 },
  hazmat: { enabled: false, price: 8.44 },
  force_majeure: { enabled: false, price: 4.0 },
  crv: { enabled: false, price: 1.2 },
  wa_syrup: { enabled: false, price: 3.0 },
  ssb: { enabled: false },
};

// Mirrors DEFAULT_SEA_SSBT in netlify/functions/_lib/seattle-ssbt.ts.
const DEFAULT_SEA_SSBT: SeaSsbtSetting = {
  enabled: false,
  cities: ['Seattle'],
  prices: { bib_3g: 40.32, bib_5g: 67.2, case_24: 5.04 },
};

// Mirrors the server defaults in netlify/functions/_lib/ssb-fee.ts — shown
// when no override is stored yet, so the admin edits from the real baseline.
const DEFAULT_SSB_CITIES = ['San Francisco', 'Oakland', 'Berkeley'];
const DEFAULT_SSB_ZIPS = ['94102-94188', '94601-94621', '94701-94720'];
const SSB_ZIP_ENTRY = /^\d{5}(\s*-\s*\d{5})?$/;

// Refractor's Pricing Control Center — where price books + customer contracts
// are managed. The hash deep-links (#pricing?tab=contracts&contract=<id> /
// &new=1) are handled by Refractor's PricingPage.
const REFRACTOR_PRICING_URL = 'https://alamedapointbg.com/margin/#pricing';

/**
 * /admin/company, one tab per job (Sky, 2026-10-31: "clean up the company
 * settings page like the returned payments page — organize, tabs, alert tags").
 * Built from the same pieces as /admin/payments. A red count on a tab means
 * someone has to act (an order desk that can't receive, sign-up requests
 * waiting); a grey tag only says how it's set ("3 on", "Off"); an amber tag
 * says a switch is off or the tab has unsaved edits. The form tabs (Billing
 * emails, Order fees, Order desk) save together from the one Save button in the
 * page header. ?tab= picks the tab.
 */
type TabKey = 'emails' | 'fees' | 'pricing' | 'desk' | 'phone' | 'syncs' | 'signups';
const TAB_KEYS: TabKey[] = ['emails', 'fees', 'pricing', 'desk', 'phone', 'syncs', 'signups'];

type FormGroup = 'emails' | 'fees' | 'desk';

interface CompanyForm {
  companyName: string;
  fromName: string;
  remittance: string;
  accounting: string;
  replyTo: string;
  creditRecipients: string;
  sendDay: number;
  remindersGlobal: boolean;
  statementsGlobal: boolean;
  schedule: ReminderStage[];
  fees: OrderFees;
  ssbCities: string;
  ssbZips: string;
  seaCities: string;
  deskEnabled: boolean;
  deskInbound: string;
  deskAllowlist: string;
  deskReviewers: string;
  deskCopyTo: string;
  deskHints: string;
}

/** The form as the saved settings fill it. Also the baseline "unsaved" compares with. */
function formOf(data: CompanySettingsData): CompanyForm {
  // Stored override wins (even an empty list — that means "none by city/ZIP");
  // absent key = never customized → show the built-in defaults.
  const ssb = data.order_fees?.ssb;
  const desk = data.order_desk;
  return {
    companyName: data.company_name,
    fromName: data.from_name,
    remittance: data.remittance_email ?? '',
    accounting: data.accounting_email ?? '',
    replyTo: data.reply_to ?? '',
    creditRecipients: (data.credit_request_recipients ?? []).join(', '),
    sendDay: data.statement_send_day,
    remindersGlobal: data.reminders_enabled_global,
    statementsGlobal: data.statements_enabled_global,
    schedule: data.reminder_schedule,
    fees: { ...DEFAULT_FEES, ...(data.order_fees ?? {}) },
    ssbCities: (ssb?.cities ?? DEFAULT_SSB_CITIES).join(', '),
    ssbZips: (ssb?.zips ?? DEFAULT_SSB_ZIPS).join(', '),
    seaCities: (data.order_fees?.sea_ssbt?.cities ?? DEFAULT_SEA_SSBT.cities).join(', '),
    deskEnabled: desk?.enabled ?? false,
    deskInbound: desk?.inbound_address ?? '',
    deskAllowlist: desk ? (desk.staff_allowlist ?? []).join(', ') : 'brixbev.com, alamedapointbg.com',
    deskReviewers: desk ? (desk.review_emails ?? []).join(', ') : '',
    deskCopyTo: desk ? (desk.copy_to ?? '') : 'service@brixbev.com',
    deskHints: desk ? (desk.extraction_hints ?? '') : '',
  };
}

function groupKeys(f: CompanyForm): Record<FormGroup, string> {
  return {
    emails: JSON.stringify([
      f.companyName, f.fromName, f.remittance, f.accounting, f.replyTo, f.creditRecipients,
      f.sendDay, f.remindersGlobal, f.statementsGlobal, f.schedule,
    ]),
    fees: JSON.stringify([f.fees, f.ssbCities, f.ssbZips, f.seaCities]),
    desk: JSON.stringify([f.deskEnabled, f.deskInbound, f.deskAllowlist, f.deskReviewers, f.deskCopyTo, f.deskHints]),
  };
}

export default function AdminCompany() {
  const { data, isLoading, error } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();

  // Sync product catalog — the manual twin of the daily catalog-sync cron
  // (10:00 UTC). Netlify won't serve a scheduled function over HTTP, hence the
  // separate admin endpoint. Safe to press: the sync never writes `orderable`,
  // so a brand-new QBO item still lands hidden until someone reviews it.
  const [catalogSyncing, setCatalogSyncing] = useState(false);
  const [catalogNotice, setCatalogNotice] = useState<string | null>(null);
  async function syncCatalog() {
    setCatalogSyncing(true);
    setCatalogNotice(null);
    try {
      const r = await adminFetch<{ upserted?: number; total_active_ops_items?: number }>(
        'admin-catalog-sync',
        { method: 'POST', body: JSON.stringify({}) },
      );
      setCatalogNotice(
        `Catalog updated — ${r.upserted ?? 0} item${r.upserted === 1 ? '' : 's'} refreshed from QuickBooks. Any brand-new item is hidden until you mark it orderable.`,
      );
    } catch (e) {
      setCatalogNotice(
        `Couldn't sync the catalog — tonight's run will pick it up. (${e instanceof Error ? e.message : 'error'})`,
      );
    } finally {
      setCatalogSyncing(false);
    }
  }

  // Sync equipment from ERLS — the manual twin of the nightly
  // sync-equipment-rentals cron (10:00 UTC). ERLS owns the asset record;
  // ops.equipment_assets is the echo BrixSD job cards and the customer
  // Equipment page both read, so a stale echo hides a machine that is
  // physically installed.
  //
  // ⚠ This button exists because ops.fn_equipment_mirror_health() TELLS the
  // operator to press it. The check shipped naming "Company -> Sync equipment
  // from ERLS" while nothing here was wired, so its own advice was a dead end
  // — the reason a watcher's instruction and the control it names have to land
  // in the same change.
  const [eqSyncing, setEqSyncing] = useState(false);
  const [eqNotice, setEqNotice] = useState<string | null>(null);
  async function syncEquipment() {
    setEqSyncing(true);
    setEqNotice(null);
    try {
      const r = await adminFetch<{
        assets_upserted?: number;
        assets_retired?: number;
        retire_refused?: string | null;
        contracts_upserted?: number;
        sf_backfill_remaining?: number;
      }>('admin-sync-equipment', { method: 'POST', body: JSON.stringify({}) });
      const bits = [
        `${r.assets_upserted ?? 0} asset${r.assets_upserted === 1 ? '' : 's'}`,
        `${r.contracts_upserted ?? 0} contract${r.contracts_upserted === 1 ? '' : 's'}`,
      ];
      if (r.assets_retired) bits.push(`${r.assets_retired} retired`);
      // A refusal is the guard doing its job, not a failure — say which guard
      // stopped it, because "nothing was retired" and "we refused to retire"
      // want different responses from whoever pressed this.
      setEqNotice(
        `Equipment refreshed from ERLS — ${bits.join(', ')}.` +
          (r.retire_refused ? ` Retirement was held back: ${r.retire_refused}` : '') +
          (r.sf_backfill_remaining
            ? ` ${r.sf_backfill_remaining} Service Fusion link${r.sf_backfill_remaining === 1 ? '' : 's'} left for the next run.`
            : ''),
      );
    } catch (e) {
      setEqNotice(
        `Couldn't reach ERLS — tonight's run will pick it up. (${e instanceof Error ? e.message : 'error'})`,
      );
    } finally {
      setEqSyncing(false);
    }
  }

  const [companyName, setCompanyName] = useState('');
  const [fromName, setFromName] = useState('');
  const [remittance, setRemittance] = useState('');
  const [accounting, setAccounting] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [creditRecipients, setCreditRecipients] = useState('');
  const [creditErr, setCreditErr] = useState<string | null>(null);
  const [sendDay, setSendDay] = useState(5);
  const [remindersGlobal, setRemindersGlobal] = useState(true);
  const [statementsGlobal, setStatementsGlobal] = useState(true);
  const [schedule, setSchedule] = useState<ReminderStage[]>([]);
  const [fees, setFees] = useState<OrderFees>(DEFAULT_FEES);
  const [ssbCities, setSsbCities] = useState(DEFAULT_SSB_CITIES.join(', '));
  const [ssbZips, setSsbZips] = useState(DEFAULT_SSB_ZIPS.join(', '));
  const [ssbZipErr, setSsbZipErr] = useState<string | null>(null);
  const [seaCities, setSeaCities] = useState(DEFAULT_SEA_SSBT.cities.join(', '));
  // Order desk (staff-forwarded email order intake)
  const [deskEnabled, setDeskEnabled] = useState(false);
  const [deskInbound, setDeskInbound] = useState('');
  const [deskAllowlist, setDeskAllowlist] = useState('brixbev.com, alamedapointbg.com');
  const [deskReviewers, setDeskReviewers] = useState('');
  const [deskCopyTo, setDeskCopyTo] = useState('service@brixbev.com');
  const [deskHints, setDeskHints] = useState('');
  const [deskErr, setDeskErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const setFlatFee = (
    key: 'fuel' | 'hazmat' | 'force_majeure' | 'crv' | 'wa_syrup',
    patch: Partial<{ enabled: boolean; price: number }>,
  ) =>
    setFees((f) => ({
      ...f,
      [key]: { ...(f[key] ?? DEFAULT_FEES[key]!), ...patch },
    }));

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  // Company-wide pricing snapshot from the central control center.
  const { data: pricing } = useQuery<{
    ok: boolean;
    standard: Array<{ qbo_item_id: string; item_name: string; unit_price: number }>;
    contracts: Array<{ id: string; name: string; locations: number; items: number; active: boolean }>;
  }>({
    queryKey: ['admin', 'pricing-overview'],
    queryFn: () => adminFetch('admin-pricing-overview'),
    staleTime: 60 * 1000,
  });

  // Export every item's prices (list + each level) as a CSV — the spreadsheet
  // used to push a price change into Service Fusion by hand (SF has no pricing API).
  async function downloadPriceList() {
    setExporting(true);
    setExportErr(null);
    try {
      const res = await adminFetch<{
        ok: true;
        levels: string[];
        rows: Array<{
          sku: string | null;
          name: string;
          sf_item_name: string | null;
          qbo_item_id: number | null;
          unit: string | null;
          category: string | null;
          list_price: number | null;
          levels: Record<string, number | null>;
        }>;
      }>('admin-export-price-list');

      const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const cols = ['SKU', 'Item Name', 'SF Item Name', 'QBO Item ID', 'Unit', 'Category', 'List Price', ...res.levels];
      const lines = [cols.map(esc).join(',')];
      for (const r of res.rows) {
        lines.push(
          [r.sku, r.name, r.sf_item_name, r.qbo_item_id, r.unit, r.category, r.list_price, ...res.levels.map((l) => r.levels[l])]
            .map(esc)
            .join(','),
        );
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brix-price-list-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }


  // Hydrate the form once settings arrive.
  useEffect(() => {
    if (!data) return;
    const f = formOf(data);
    setCompanyName(f.companyName);
    setFromName(f.fromName);
    setRemittance(f.remittance);
    setAccounting(f.accounting);
    setReplyTo(f.replyTo);
    setCreditRecipients(f.creditRecipients);
    setSendDay(f.sendDay);
    setRemindersGlobal(f.remindersGlobal);
    setStatementsGlobal(f.statementsGlobal);
    setSchedule(f.schedule);
    setFees(f.fees);
    setSsbCities(f.ssbCities);
    setSsbZips(f.ssbZips);
    setSeaCities(f.seaCities);
    setDeskEnabled(f.deskEnabled);
    setDeskInbound(f.deskInbound);
    setDeskAllowlist(f.deskAllowlist);
    setDeskReviewers(f.deskReviewers);
    setDeskCopyTo(f.deskCopyTo);
    setDeskHints(f.deskHints);
  }, [data]);

  // What changed since the last load, per tab — drives the "Unsaved" tags and
  // whether Save is live. Compared against the same shape the form was filled
  // from, so a field typed back to its saved value reads as unchanged.
  const current: CompanyForm = {
    companyName, fromName, remittance, accounting, replyTo, creditRecipients, sendDay,
    remindersGlobal, statementsGlobal, schedule, fees, ssbCities, ssbZips, seaCities,
    deskEnabled, deskInbound, deskAllowlist, deskReviewers, deskCopyTo, deskHints,
  };
  const baseline = useMemo(() => (data ? groupKeys(formOf(data)) : null), [data]);
  const now = groupKeys(current);
  const dirty: Record<FormGroup, boolean> = {
    emails: !!baseline && baseline.emails !== now.emails,
    fees: !!baseline && baseline.fees !== now.fees,
    desk: !!baseline && baseline.desk !== now.desk,
  };
  const anyDirty = dirty.emails || dirty.fees || dirty.desk;

  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = asked && TAB_KEYS.includes(asked) ? asked : 'emails';
  const setTab = (k: TabKey) => setSearchParams({ tab: k }, { replace: true });

  const handleSave = () => {
    setSaved(false);
    const cities = ssbCities.split(',').map((c) => c.trim()).filter(Boolean);
    const zips = ssbZips.split(',').map((z) => z.trim()).filter(Boolean);
    const badZip = zips.find((z) => !SSB_ZIP_ENTRY.test(z));
    if (badZip) {
      setSsbZipErr(`"${badZip}" isn't a 5-digit ZIP or ZIP range (e.g. 94110 or 94102-94188)`);
      setTab('fees');
      return;
    }
    setSsbZipErr(null);

    const csv = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);
    const deskAllow = csv(deskAllowlist);
    const deskReview = csv(deskReviewers);
    const inbound = deskInbound.trim().toLowerCase();
    if (deskEnabled && !inbound) {
      setDeskErr('The order desk needs an inbound address before it can be turned on.');
      setTab('desk');
      return;
    }
    if (deskEnabled && deskAllow.length === 0) {
      setDeskErr('Add at least one staff domain — only our own addresses may forward orders in.');
      setTab('desk');
      return;
    }
    setDeskErr(null);

    const creditList = creditRecipients
      .split(/[,;\n]/)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    const badCredit = creditList.find((x) => !/^[^\s@,;<>]+@[^\s@,;<>]+\.[A-Za-z]{2,}$/.test(x));
    if (creditList.length === 0) {
      setCreditErr('Keep at least one address — a request emailed to nobody is a request lost.');
      setTab('emails');
      return;
    }
    if (badCredit) {
      setCreditErr(`"${badCredit}" isn't an email address.`);
      setTab('emails');
      return;
    }
    setCreditErr(null);

    const patch: CompanySettingsPatch = {
      company_name: companyName.trim(),
      from_name: fromName.trim(),
      remittance_email: remittance.trim() === '' ? null : remittance.trim(),
      accounting_email: accounting.trim() === '' ? null : accounting.trim(),
      reply_to: replyTo.trim() === '' ? null : replyTo.trim(),
      credit_request_recipients: creditList,
      statement_send_day: sendDay,
      reminders_enabled_global: remindersGlobal,
      statements_enabled_global: statementsGlobal,
      reminder_schedule: schedule
        .slice()
        .sort((a, b) => a.offset_days - b.offset_days),
      order_fees: {
        ...fees,
        ssb: { enabled: fees.ssb.enabled, cities, zips },
        sea_ssbt: {
          ...(fees.sea_ssbt ?? DEFAULT_SEA_SSBT),
          cities: seaCities.split(',').map((c) => c.trim()).filter(Boolean),
        },
      },
      order_desk: {
        enabled: deskEnabled,
        inbound_address: inbound || null,
        staff_allowlist: deskAllow,
        review_emails: deskReview,
        copy_to: deskCopyTo.trim() === '' ? null : deskCopyTo.trim().toLowerCase(),
        extraction_hints: deskHints.trim() === '' ? null : deskHints.trim(),
      },
    };
    updateMutation.mutate(patch, { onSuccess: () => setSaved(true) });
  };

  const addStage = () =>
    setSchedule((s) => [...s, { offset_days: 0, tone: 'friendly' }]);
  const removeStage = (i: number) =>
    setSchedule((s) => s.filter((_, idx) => idx !== i));
  const updateStage = (i: number, patch: Partial<ReminderStage>) =>
    setSchedule((s) => s.map((stage, idx) => (idx === i ? { ...stage, ...patch } : stage)));

  const signups = useAccessRequests();
  const { data: comms } = useCommsPause();
  const { data: voiceMode } = useVoiceMode();

  if (isLoading) {
    return (
      <div className="px-6 py-10 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading company settings…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-6">
        <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
          {error instanceof Error ? error.message : 'Failed to load settings'}
        </div>
      </div>
    );
  }

  // What the saved settings say is wrong, as opposed to what is merely off.
  // A red count means someone has to act; a grey tag only says how it's set.
  const savedDesk = data?.order_desk;
  const deskProblems = [
    savedDesk?.enabled && !savedDesk.inbound_address
      ? 'It is on but has no inbound address, so nothing can be forwarded to it.'
      : null,
    savedDesk?.enabled && data?._env && !data._env.order_desk_secret
      ? data._env.inbound_secret_fallback
        ? 'RESEND_ORDER_DESK_SECRET is not set on Netlify. Only the EDI route’s secret is, and Resend signs order-desk mail with a different one, so forwards will be rejected.'
        : 'RESEND_ORDER_DESK_SECRET is not set on Netlify, so forwarded emails can’t be verified.'
      : null,
  ].filter((x): x is string => !!x);
  const feesOn =
    (['fuel', 'hazmat', 'force_majeure', 'crv', 'wa_syrup'] as const).filter((k) => fees[k]?.enabled).length +
    (fees.ssb.enabled ? 1 : 0) +
    (fees.sea_ssbt?.enabled ? 1 : 0);
  const switchesOff = [!remindersGlobal && 'Reminders', !statementsGlobal && 'Statements'].filter(Boolean);
  const unsaved = (g: FormGroup, fallback?: PayTab<TabKey>['tag']) =>
    dirty[g] ? { text: 'Unsaved', tone: 'amber' as const } : fallback;

  const tabs: Array<PayTab<TabKey>> = [
    {
      key: 'emails',
      label: 'Billing emails',
      tag: unsaved(
        'emails',
        comms?.paused
          ? { text: 'Emails paused', tone: 'amber' }
          : switchesOff.length
            ? { text: `${switchesOff.join(' & ')} off`, tone: 'amber' }
            : undefined,
      ),
    },
    {
      key: 'fees',
      label: 'Order fees',
      tag: unsaved('fees', { text: feesOn === 0 ? 'All off' : `${feesOn} on` }),
    },
    {
      key: 'pricing',
      label: 'Pricing',
      tag: pricing
        ? (() => {
            const n = pricing.contracts.filter((c) => c.active).length;
            return { text: `${n} contract${n === 1 ? '' : 's'}` };
          })()
        : undefined,
    },
    {
      key: 'desk',
      label: 'Order desk',
      alert: deskProblems.length,
      tag: unsaved('desk', { text: deskEnabled ? 'On' : 'Off' }),
    },
    {
      key: 'phone',
      label: 'Phone agent',
      tag: voiceMode
        ? { text: voiceMode === 'off' ? 'Verification off' : `Code by ${voiceMode === 'sms' ? 'text' : 'email'}` }
        : undefined,
    },
    { key: 'syncs', label: 'Syncs' },
    { key: 'signups', label: 'Sign-up requests', alert: signups.data?.length ?? 0 },
  ];
  const unsavedNames = tabs.filter((t) => t.tag?.text === 'Unsaved').map((t) => t.label);

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-lg font-bold text-brix-ink flex items-center gap-2">
          Company
          <InfoDot text="Settings for the whole portal, one tab per job. Billing emails, Order fees and Order desk are edited here and saved together with the Save button at the top right; the other tabs act straight away." />
        </h1>
        <div className="flex items-center gap-3">
          {anyDirty && !updateMutation.isPending && (
            <span className="text-xs font-semibold text-amber-700">
              Unsaved changes: {unsavedNames.join(', ')}
            </span>
          )}
          {saved && !anyDirty && !updateMutation.isPending && (
            <span className="text-xs font-semibold text-emerald-600">Saved.</span>
          )}
          {updateMutation.error && (
            <span className="text-xs text-brix-warning">
              {updateMutation.error instanceof Error ? updateMutation.error.message : 'Save failed'}
            </span>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateMutation.isPending || !anyDirty}
          >
            {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {updateMutation.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <PayTabs tabs={tabs} value={tab} onChange={setTab} />

        {tab === 'emails' && (
          <div className="space-y-4">
            {/* The emergency stop. Billing emails is the tab the page opens on, so it is
                still the first thing here; while paused, a red banner shows on every page. */}
            <CommsPauseCard />
            <Section title="Sender identity" desc="How billing emails are addressed and where replies go.">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Company name">
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </Field>
                <Field label="From name">
                  <Input value={fromName} onChange={(e) => setFromName(e.target.value)} />
                </Field>
              </div>
              <Field label="Primary email (AR inbox · Reply-To · remit-to on statements)">
                <Input
                  value={remittance}
                  onChange={(e) => setRemittance(e.target.value)}
                  placeholder="ar@brixbev.com"
                  type="email"
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Accounting email (optional · BCC on every billing email)">
                  <Input
                    value={accounting}
                    onChange={(e) => setAccounting(e.target.value)}
                    placeholder="accounting@brixbev.com"
                    type="email"
                  />
                </Field>
                <Field label="Reply-To override (optional)">
                  <Input
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="Defaults to the primary email"
                    type="email"
                  />
                </Field>
              </div>
            </Section>

            <Section
              title="Statements & reminders"
              desc="These switches gate every send. Each customer still opts in on their own account."
            >
              <ToggleRow
                label="Overdue reminders"
                desc="The daily job that emails reminders on the schedule below"
                enabled={remindersGlobal}
                onChange={setRemindersGlobal}
              />
              <ToggleRow
                label="Monthly statements"
                desc="The monthly open-items statement"
                enabled={statementsGlobal}
                onChange={setStatementsGlobal}
              />
              <Field label="Statement send day (day of month, 1–28)">
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={sendDay}
                  onChange={(e) => setSendDay(Math.max(1, Math.min(28, Number(e.target.value) || 1)))}
                  className="max-w-[120px]"
                />
              </Field>
            </Section>

            <Section
              title="Reminder schedule"
              desc="Each stage fires relative to the invoice's due date: negative is before it, positive is past due. Firm is direct but professional."
              actions={
                <Button variant="secondary" size="sm" onClick={addStage}>
                  <Plus size={13} /> Add stage
                </Button>
              }
            >
              {schedule.length === 0 ? (
                <Attention count={1}>no stages, so no reminder will ever be sent. Add one.</Attention>
              ) : (
                <div className="space-y-2">
                  {schedule.map((stage, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2"
                    >
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          value={stage.offset_days}
                          onChange={(e) => updateStage(i, { offset_days: Number(e.target.value) || 0 })}
                          className="w-20"
                        />
                        <span className="text-xs text-brix-muted whitespace-nowrap">days from due</span>
                      </div>
                      <div className="flex gap-1">
                        {(['friendly', 'firm'] as const).map((tone) => (
                          <button
                            key={tone}
                            type="button"
                            onClick={() => updateStage(i, { tone })}
                            className={cn(
                              'px-2.5 py-1 rounded-md text-xs font-semibold capitalize cursor-pointer transition-colors',
                              stage.tone === tone
                                ? 'bg-brix-primary text-white'
                                : 'bg-brix-surface-alt text-brix-muted hover:text-brix-ink',
                            )}
                          >
                            {tone}
                          </button>
                        ))}
                      </div>
                      <span className="text-[11px] text-brix-muted flex-1">
                        {stage.offset_days < 0
                          ? `${Math.abs(stage.offset_days)} day(s) before due`
                          : stage.offset_days === 0
                            ? 'on the due date'
                            : `${stage.offset_days} day(s) past due`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeStage(i)}
                        className="text-brix-muted hover:text-brix-warning cursor-pointer"
                        aria-label="Remove stage"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section
              title="Credit requests"
              desc="When a customer asks us to apply an unapplied payment or credit, or to mail a check, this list gets the email. Someone applies it in QuickBooks and contacts the customer."
            >
              <Field label="Send credit requests to (comma-separated)">
                <Input
                  value={creditRecipients}
                  onChange={(e) => setCreditRecipients(e.target.value)}
                  placeholder="service@brixbev.com, asloan@brixbev.com, marco@brixbev.com"
                />
              </Field>
              {creditErr && <p className="text-sm text-red-600">{creditErr}</p>}
            </Section>
          </div>
        )}

        {tab === 'fees' && (
          <div className="space-y-4">
            <Section
              title="Who bills the fees"
              desc="Fees are added to every order at submit. Each one becomes a line on the Service Fusion job, and so on the invoice."
            >
              <div
                className={cn(
                  'rounded-lg',
                  fees.send_to_sf === true && 'ring-1 ring-amber-300',
                )}
              >
                <ToggleRow
                  label="The portal charges these fees"
                  desc="On: every fee below goes onto the Service Fusion job at these rates, so the portal's numbers are what the invoice bills. ⚠ Turn OFF Service Fusion's own automatic fees and taxes first, or every order is charged twice. Off: the fees are an on-screen estimate and Service Fusion's own settings bill."
                  enabled={fees.send_to_sf === true}
                  onChange={(v) => setFees((f) => ({ ...f, send_to_sf: v }))}
                />
              </div>
            </Section>

            <Section title="Delivery & gas" desc="Flat charges. Edit a price here and it changes on every new order.">
              <FeeRow
                label="Delivery fuel surcharge"
                desc="DELCHO1 · on every order (the per-order delivery charge)"
                fee={fees.fuel}
                onToggle={(v) => setFlatFee('fuel', { enabled: v })}
                onPrice={(p) => setFlatFee('fuel', { price: p })}
              />
              <FeeRow
                label="Hazmat fee"
                desc="COMPRESSED GAS DOT HAZMAT FEE · once per order with a gas cylinder"
                fee={fees.hazmat}
                onToggle={(v) => setFlatFee('hazmat', { enabled: v })}
                onPrice={(p) => setFlatFee('hazmat', { price: p })}
              />
              <FeeRow
                label="Force majeure fee"
                desc="COMPRESSED GAS FORCE MAJURE FEE · once per order with a gas cylinder"
                fee={fees.force_majeure}
                onToggle={(v) => setFlatFee('force_majeure', { enabled: v })}
                onPrice={(p) => setFlatFee('force_majeure', { price: p })}
              />
            </Section>

            <Section title="Deposits & beverage taxes" desc="Per container, and only where the law applies.">
              <FeeRow
                label="CRV (CA Redemption Value)"
                desc="CRV-24PK · $0.05/can, charged per 24-pack. Statewide."
                fee={fees.crv}
                unit="per 24-pack"
                onToggle={(v) => setFlatFee('crv', { enabled: v })}
                onPrice={(p) => setFlatFee('crv', { price: p })}
              />
              <FeeRow
                label="WA syrup tax (Washington)"
                desc="ZBTWA3G · on each 3-gallon BIB delivered to a Washington location. Statewide."
                fee={fees.wa_syrup ?? DEFAULT_FEES.wa_syrup!}
                unit="per 3-gal BIB"
                onToggle={(v) => setFlatFee('wa_syrup', { enabled: v })}
                onPrice={(p) => setFlatFee('wa_syrup', { price: p })}
              />
              <div className="space-y-2">
                <ToggleRow
                  label="Bay Area beverage tax (SSB)"
                  desc="ZBT · $0.01/finished oz on sweetened BIB and packs delivered into the cities or ZIPs below. The price comes from the QuickBooks ZBT items."
                  enabled={fees.ssb.enabled}
                  onChange={(v) => setFees((f) => ({ ...f, ssb: { ...f.ssb, enabled: v } }))}
                />
                <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5 space-y-2">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Taxed cities">
                      <Input
                        value={ssbCities}
                        onChange={(e) => setSsbCities(e.target.value)}
                        placeholder="San Francisco, Oakland, Berkeley"
                      />
                    </Field>
                    <Field label="Taxed ZIPs / ranges">
                      <Input
                        value={ssbZips}
                        onChange={(e) => {
                          setSsbZips(e.target.value);
                          setSsbZipErr(null);
                        }}
                        placeholder="94102-94188, 94601-94621, 94701-94720"
                      />
                    </Field>
                  </div>
                  {ssbZipErr && <p className="text-sm text-brix-warning">{ssbZipErr}</p>}
                  <p className="text-[11px] text-brix-muted leading-relaxed">
                    Comma-separated. A delivery owes the tax when its city matches (any case) or its ZIP
                    falls in one of these.
                  </p>
                </div>
              </div>
              <SeaSsbtRow
                value={fees.sea_ssbt ?? DEFAULT_SEA_SSBT}
                cities={seaCities}
                onCities={setSeaCities}
                onChange={(patch) =>
                  setFees((f) => ({ ...f, sea_ssbt: { ...(f.sea_ssbt ?? DEFAULT_SEA_SSBT), ...patch } }))
                }
              />
            </Section>
          </div>
        )}

        {tab === 'pricing' && (
          <Section
            title="Pricing"
            desc="Standard prices and each customer's contract live in Refractor → Pricing; each store's prices show on its customer's Pricing tab. Service Fusion has no pricing API, so a change gets there through the CSV."
            actions={
              <>
                <a
                  href={`${REFRACTOR_PRICING_URL}?tab=contracts&new=1`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brix-primary hover:underline"
                >
                  <Plus size={14} /> Add pricing in Refractor
                </a>
                <Button variant="secondary" onClick={downloadPriceList} disabled={exporting}>
                  {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  {exporting ? 'Building…' : 'Export for Service Fusion (CSV)'}
                </Button>
              </>
            }
          >
            {exportErr && <p className="text-sm text-brix-warning">{exportErr}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2">
                <p className="text-[11px] text-brix-muted">BX-1 standard list</p>
                <p className="text-lg font-semibold text-brix-ink">
                  {pricing ? `${pricing.standard.length} items` : '—'}
                </p>
              </div>
              <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2">
                <p className="text-[11px] text-brix-muted">Active customer contracts</p>
                <p className="text-lg font-semibold text-brix-ink">
                  {pricing ? `${pricing.contracts.filter((c) => c.active).length}` : '—'}
                </p>
              </div>
            </div>
            <TableState
              loading={!pricing}
              empty={!!pricing && pricing.contracts.length === 0}
              emptyText="No customer contracts yet."
            >
              <PayTable minWidth={420}>
                <thead>
                  <tr>
                    <Th>Contract</Th>
                    <Th right>Locations</Th>
                    <Th right>Items</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brix-border">
                  {(pricing?.contracts ?? []).map((c) => (
                    <tr key={c.id ?? c.name}>
                      <Td>
                        {c.id ? (
                          <a
                            href={`${REFRACTOR_PRICING_URL}?tab=contracts&contract=${encodeURIComponent(c.id)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-brix-primary hover:underline"
                            title={`Open ${c.name} in the Pricing Control Center`}
                          >
                            {c.name}
                          </a>
                        ) : (
                          <span className="text-brix-ink">{c.name}</span>
                        )}
                        {!c.active && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                            Inactive
                          </span>
                        )}
                      </Td>
                      <Td right mono>{c.locations}</Td>
                      <Td right mono>{c.items}</Td>
                    </tr>
                  ))}
                </tbody>
              </PayTable>
            </TableState>
          </Section>
        )}

        {tab === 'desk' && (
          <Section
            title="Order desk (forwarded emails)"
            desc="Forward a customer's emailed order to one address and it arrives already read (account, items and delivery day worked out) with an Accept button. Nothing reaches Service Fusion until a person accepts it."
            actions={
              <Link
                to="/admin/order-desk"
                className="inline-flex items-center gap-1 text-sm font-semibold text-brix-primary hover:underline"
              >
                Open the Order desk →
              </Link>
            }
          >
            <Attention count={deskProblems.length}>{deskProblems.join(' ')}</Attention>

            <ToggleRow
              label="Read forwarded order emails"
              desc="Delivery dates always come from the customer's delivery schedule, never from the email."
              enabled={deskEnabled}
              onChange={setDeskEnabled}
            />

            <IntakeCheck env={data?._env} />

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Inbound address">
                <Input
                  value={deskInbound}
                  onChange={(e) => setDeskInbound(e.target.value)}
                  placeholder="aiorders@alamedapointbg.com"
                />
                <p className="text-[11px] text-brix-muted mt-1">Staff forward customer emails here.</p>
              </Field>
              <Field label="Ops copy">
                <Input
                  value={deskCopyTo}
                  onChange={(e) => setDeskCopyTo(e.target.value)}
                  placeholder="service@brixbev.com"
                />
                <p className="text-[11px] text-brix-muted mt-1">Always copied on the proposal.</p>
              </Field>
            </div>

            <Field label="Who may forward (staff only)">
              <Input
                value={deskAllowlist}
                onChange={(e) => setDeskAllowlist(e.target.value)}
                placeholder="brixbev.com, alamedapointbg.com"
              />
              <p className="text-[11px] text-brix-muted mt-1">
                Domains or full addresses, comma-separated. Anything from outside this list is recorded
                and dropped, so a customer's reply or auto-reply can't become an order.
              </p>
            </Field>

            <Field label="Who decides">
              <Input
                value={deskReviewers}
                onChange={(e) => setDeskReviewers(e.target.value)}
                placeholder="joel@brixbev.com"
              />
              <p className="text-[11px] text-brix-muted mt-1">
                Extra recipients of the Accept / Open-to-edit email, comma-separated. Whoever forwarded it
                always gets it.
              </p>
            </Field>

            <Field label="House rules for reading emails (optional)">
              <textarea
                value={deskHints}
                onChange={(e) => setDeskHints(e.target.value)}
                rows={3}
                placeholder="e.g. 'a bare flavour with no unit means a 3-gal BIB'"
                className="w-full rounded-lg border border-brix-border bg-white px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-brix-muted mt-1">
                Added to the reading instructions. Keep it to real conventions: item matching already
                prefers what each customer actually buys.
              </p>
            </Field>

            {deskErr && <p className="text-sm text-brix-warning">{deskErr}</p>}
          </Section>
        )}

        {tab === 'syncs' && (
          <div className="space-y-4">
            <Section
              title="Product catalog"
              desc="Products and prices come from QuickBooks every night. Sync now if you've just added or repriced something and want it in the portal today."
              actions={
                <>
                  <Button variant="secondary" onClick={() => void syncCatalog()} disabled={catalogSyncing}>
                    {catalogSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {catalogSyncing ? 'Syncing…' : 'Sync product catalog'}
                  </Button>
                  <InfoDot text="Pulls every active QuickBooks item into the portal catalog. A brand-new item arrives switched OFF for ordering so you can check its name, price and category first — this never puts an unreviewed product in front of customers." />
                </>
              }
            >
              {catalogNotice && <Notice>{catalogNotice}</Notice>}
            </Section>

            <Section
              title="Equipment on customers' accounts"
              desc="Leased and rented equipment lives in ERLS and copies over every night. Sync now if a machine was just installed or picked up and you need it on the account today."
              actions={
                <>
                  <Button variant="secondary" onClick={() => void syncEquipment()} disabled={eqSyncing}>
                    {eqSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {eqSyncing ? 'Syncing…' : 'Sync equipment from ERLS'}
                  </Button>
                  <InfoDot text="Copies every asset and rental contract out of ERLS onto the customers' accounts, which is what a technician's job card and the customer's Equipment page read. ERLS stays the record — nothing you do here changes it. Equipment ERLS has deleted is removed too, but only when the whole list reads cleanly: a partial read is refused rather than obeyed, so a bad connection can never empty the account." />
                </>
              }
            >
              {eqNotice && <Notice>{eqNotice}</Notice>}
            </Section>

            {/* The Melt's SynergySuite EDI — connection test + one store's price file. */}
            <SynergySftpCard />
          </div>
        )}

        {tab === 'phone' && <VoiceVerificationCard />}

        {tab === 'signups' && <AccessRequestsPanel q={signups} />}
      </div>
    </div>
  );
}

/**
 * Live check that a forwarded email can actually get in.
 *
 * Four things have to be true and none of them are visible from this page: the
 * function deployed, a Resend inbound route pointing at it, the route's own
 * signing secret present, and that secret being the RIGHT one.
 *
 * The first three are checkable. Asking the intake distinguishes deployed from
 * routed-and-armed by status code — an UNSIGNED POST is rejected either way,
 * but *how* it is rejected is the diagnostic (401 = signatures enforced, 503 =
 * no secret at all, 404 = not deployed) — and the settings endpoint reports
 * which secret env var is actually set, since only the server can see that.
 *
 * ⚠ The fourth is NOT checkable from here, and the honest answer is to say so.
 * A 401 proves a secret is configured, not that it matches what Resend signs
 * with; the fallback to the EDI route's secret answers 401 exactly the same
 * way. Only a real forwarded email settles it. This panel therefore reports
 * "armed" rather than "verified", and warns specifically when the order-desk
 * variable is missing and the fallback is carrying it.
 *
 * Nothing is written by the probe; the request carries no signature, so the
 * intake refuses it before parsing anything.
 */
function IntakeCheck({ env }: { env?: CompanySettingsEnv }) {
  const [state, setState] = useState<'idle' | 'checking' | 'done'>('idle');
  const [result, setResult] = useState<{ tone: 'ok' | 'warn'; msg: string } | null>(null);

  const check = async () => {
    setState('checking');
    setResult(null);
    try {
      const res = await fetch('/.netlify/functions/order-email-intake', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (res.status === 404) {
        setResult({ tone: 'warn', msg: 'The intake function isn’t deployed yet.' });
      } else if (res.status === 503) {
        setResult({
          tone: 'warn',
          msg: 'Deployed, but no signing secret is configured. Set RESEND_ORDER_DESK_SECRET on Netlify to the inbound route’s whsec_… value, then redeploy.',
        });
      } else if (res.status === 401) {
        // Signatures are enforced. Which secret is doing it decides the wording.
        if (env && !env.order_desk_secret) {
          setResult({
            tone: 'warn',
            msg: env.inbound_secret_fallback
              ? 'Deployed and enforcing signatures — but only RESEND_INBOUND_SECRET is set, which is the EDI route’s secret. Resend signs order-desk mail with a DIFFERENT secret, so forwards will be rejected. Set RESEND_ORDER_DESK_SECRET and redeploy.'
              : 'Deployed and enforcing signatures, but RESEND_ORDER_DESK_SECRET is not set.',
          });
        } else {
          setResult({
            tone: 'ok',
            msg: 'Armed — deployed, and the order-desk signing secret is configured. The first real forward confirms the secret matches.',
          });
        }
      } else {
        setResult({ tone: 'warn', msg: `Unexpected reply (HTTP ${res.status}).` });
      }
    } catch {
      setResult({ tone: 'warn', msg: 'Could not reach the intake.' });
    }
    setState('done');
  };

  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface/60 px-3 py-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" variant="secondary" size="sm" onClick={check} disabled={state === 'checking'}>
          {state === 'checking' ? (
            <>
              <Loader2 size={13} className="animate-spin mr-1.5" /> Checking…
            </>
          ) : (
            'Check the intake'
          )}
        </Button>
        <span className="text-[11px] text-brix-muted">
          Confirms the deploy, the inbound route and the signing secret.
        </span>
      </div>
      {result && (
        <p
          className={cn(
            'text-[12px] mt-2 leading-snug',
            result.tone === 'ok' ? 'text-emerald-700' : 'text-amber-700',
          )}
        >
          {result.tone === 'ok' ? '✓ ' : '⚠ '}
          {result.msg}
        </p>
      )}
    </div>
  );
}

/**
 * One card per job inside a tab, laid out like the Payments tabs: title and
 * what it does on the left, its buttons always at the right.
 */
function Section({
  title,
  desc,
  actions,
  children,
}: {
  title: string;
  desc?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface p-5 space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 max-w-2xl flex-1">
          <h2 className="text-base font-semibold text-brix-ink">{title}</h2>
          {desc && <p className="text-xs leading-relaxed text-brix-muted mt-0.5">{desc}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 md:justify-end">{actions}</div>}
      </div>
      {children && <div className="space-y-3">{children}</div>}
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">{children}</div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FeeRow({
  label,
  desc,
  fee,
  onToggle,
  onPrice,
  unit = 'per order',
}: {
  label: string;
  desc: string;
  fee: { enabled: boolean; price: number };
  onToggle: (v: boolean) => void;
  onPrice: (p: number) => void;
  unit?: string;
}) {
  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-brix-ink leading-tight">{label}</div>
          <div className="text-[11px] text-brix-muted leading-tight mt-0.5">{desc}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={fee.enabled}
          aria-label={`${label}: ${fee.enabled ? 'on' : 'off'}`}
          onClick={() => onToggle(!fee.enabled)}
          className={cn(
            'relative shrink-0 w-10 h-6 rounded-full transition-colors cursor-pointer',
            fee.enabled ? 'bg-brix-primary' : 'bg-slate-300',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
              fee.enabled && 'translate-x-4',
            )}
          />
        </button>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
          Price&nbsp;$
        </span>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={fee.price}
          onChange={(e) => onPrice(Math.max(0, Number(e.target.value) || 0))}
          className="max-w-[120px]"
          disabled={!fee.enabled}
        />
        <span className="text-[11px] text-brix-muted">{unit}</span>
      </div>
    </div>
  );
}

/**
 * Seattle SSBT — a price per container (not one flat price), so it is its own
 * row rather than a FeeRow. Prices are $0.0175 × finished ounces.
 */
function SeaSsbtRow({
  value,
  cities,
  onCities,
  onChange,
}: {
  value: SeaSsbtSetting;
  cities: string;
  onCities: (v: string) => void;
  onChange: (patch: Partial<SeaSsbtSetting>) => void;
}) {
  const sizes = [
    { key: 'bib_3g', label: '3-gal BIB', oz: 2304 },
    { key: 'bib_5g', label: '5-gal BIB', oz: 3840 },
    { key: 'case_24', label: '24-pack', oz: 288 },
  ] as const;
  return (
    <div className="space-y-2">
      <ToggleRow
        label="Seattle beverage tax (SSBT)"
        desc="ZBTSEA — $0.0175/finished oz on every SUGARED syrup and can (sodas and mixers) delivered to the cities below. Diet, Sugar Free and Mango Sparkling are exempt. Charged on top of the WA syrup tax."
        enabled={value.enabled}
        onChange={(v) => onChange({ enabled: v })}
      />
      <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5 space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {sizes.map((s) => (
            <Field key={s.key} label={`${s.label} — $ (${s.oz.toLocaleString()} oz)`}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={value.prices[s.key]}
                onChange={(e) =>
                  onChange({ prices: { ...value.prices, [s.key]: Math.max(0, Number(e.target.value) || 0) } })
                }
              />
            </Field>
          ))}
        </div>
        <Field label="Taxed cities (comma-separated)">
          <Input value={cities} onChange={(e) => onCities(e.target.value)} placeholder="Seattle" />
        </Field>
        <p className="text-[11px] text-brix-muted leading-relaxed">
          The delivery CITY decides — there is no ZIP fallback, because Seattle&apos;s ZIPs are
          shared with suburbs that levy no tax. To exempt one item (e.g. a 100% juice), set its
          catalog override (orders.catalog.sea_ssbt_taxable = false).
        </p>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  desc,
  enabled,
  onChange,
}: {
  label: string;
  desc: string;
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5">
      <div>
        <div className="text-sm font-semibold text-brix-ink leading-tight">{label}</div>
        <div className="text-[11px] text-brix-muted leading-tight mt-0.5">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${label}: ${enabled ? 'on' : 'off'}`}
        onClick={() => onChange(!enabled)}
        className={cn(
          'relative shrink-0 w-10 h-6 rounded-full transition-colors cursor-pointer',
          enabled ? 'bg-brix-primary' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            enabled && 'translate-x-4',
          )}
        />
      </button>
    </div>
  );
}

interface AccessRequest {
  id: string;
  business_name: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  note: string | null;
  source: string;
  status: string;
  created_at: string;
}

/** Open sign-up requests. The page reads it too, for the tab's red count. */
function useAccessRequests() {
  return useQuery<AccessRequest[]>({
    queryKey: ['admin', 'access-requests', 'new'],
    queryFn: async () =>
      (await adminFetch<{ ok: true; requests: AccessRequest[] }>('admin-access-requests?status=new')).requests,
    staleTime: 60 * 1000,
  });
}

/**
 * New-customer sign-up requests from /welcome-back ("I'm new / that's not me",
 * plus legacy logins recognized for accounts not yet enabled). Superadmin can
 * mark each handled or dismiss it. Read/write via admin-access-requests.
 */
function AccessRequestsPanel({ q }: { q: ReturnType<typeof useAccessRequests> }) {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const rows = q.data ?? [];

  async function act(id: string, action: 'handle' | 'dismiss') {
    setBusyId(id);
    setErr(null);
    try {
      await adminFetch('admin-access-requests', {
        method: 'POST',
        body: JSON.stringify({ id, action }),
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'access-requests', 'new'] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Section
      title="New-customer sign-up requests"
      desc="From the sign-up page (/welcome-back): people who don't have a login yet, or whose old account isn't switched on. Set them up, then mark the request handled."
      actions={
        <Button variant="secondary" size="sm" onClick={() => void q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
        </Button>
      }
    >
      <Attention count={rows.length}>each one is someone waiting to hear back about ordering.</Attention>
      {err && <p className="text-sm text-brix-warning">{err}</p>}
      <TableState loading={q.isLoading} error={q.error} empty={rows.length === 0} emptyText="No open requests.">
        <PayTable minWidth={640}>
          <thead>
            <tr>
              <Th>Business</Th>
              <Th>Contact</Th>
              <Th>Received</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {rows.map((r) => (
              <tr key={r.id}>
                <Td>
                  <div className="font-semibold text-brix-ink">
                    {r.business_name || r.contact_name || r.email || 'Unnamed request'}
                    {r.source === 'legacy_not_enabled' && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                        Legacy — not enabled
                      </span>
                    )}
                  </div>
                  {r.note && <div className="mt-0.5 text-xs text-slate-600">{r.note}</div>}
                </Td>
                <Td className="text-xs text-brix-muted">
                  {[r.contact_name, r.email, r.phone].filter(Boolean).join(' · ')}
                </Td>
                <Td className="whitespace-nowrap text-xs text-brix-muted">
                  {new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                </Td>
                <ActionsTd>
                  <Button variant="secondary" size="sm" onClick={() => act(r.id, 'handle')} disabled={busyId === r.id}>
                    Mark handled
                  </Button>
                  <button
                    type="button"
                    onClick={() => act(r.id, 'dismiss')}
                    disabled={busyId === r.id}
                    className="text-xs font-semibold text-brix-muted hover:text-brix-ink"
                  >
                    Dismiss
                  </button>
                </ActionsTd>
              </tr>
            ))}
          </tbody>
        </PayTable>
      </TableState>
    </Section>
  );
}
